import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/format";

/** Live rate, shared across every placement — one fetch per minute for the
 * whole page, never one per strip instance. Placement is an admin setting
 * (rate_strip_locations); this component only knows how to render each mode. */
let cache = null;
let inflight = null;
const listeners = new Set();

function refresh() {
  if (!inflight) {
    inflight = api.get("/rates/current")
      .then((r) => { cache = r.data; listeners.forEach((fn) => fn(cache)); })
      .catch(() => {})
      .finally(() => { inflight = null; });
  }
  return inflight;
}

export function useLiveRate() {
  const [rate, setRate] = useState(cache);
  useEffect(() => {
    const fn = (r) => setRate(r);
    listeners.add(fn);
    refresh();
    const t = setInterval(refresh, 60000);
    return () => { listeners.delete(fn); clearInterval(t); };
  }, []);
  return rate;
}

export default function RateStrip({ mode = "band" }) {
  const rate = useLiveRate();
  if (!rate) return null;
  const gold = `Gold 999 · ₹${Number(rate.gold_24k).toLocaleString("en-IN")}/g`;
  const silver = `Silver 999 · ₹${Number(rate.silver).toLocaleString("en-IN")}/g`;
  const updated = `updated ${fmtDateTime(rate.updated_at)}`;

  if (mode === "footer") {
    return (
      <p className="text-[11px] font-mono text-ivory/45" data-testid="rate-strip-footer">
        {gold} · {silver} · <span className="text-ivory/35">{updated}</span>
      </p>
    );
  }

  if (mode === "feature") {
    return (
      <section className="container-x mt-10" data-testid="rate-strip-feature">
        <div className="border border-gold/40 bg-white rounded-2xl px-6 py-8 sm:px-10 flex flex-col sm:flex-row sm:items-center gap-6">
          <div className="flex-1">
            <p className="eyebrow !text-brand">LIVE BULLION RATES</p>
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-ink mt-2">Every coin is priced off today's rate — nothing hidden</h2>
            <p className="text-sm text-ink-2 mt-2 max-w-lg">
              Metal at the live rate, a clearly shown craft charge, 3% GST. The full breakup is on every coin page before you pay.
            </p>
            <Link to="/gold-rate" className="inline-block mt-3 text-sm text-brand underline underline-offset-4 decoration-gold hover:text-brand-2 transition-colors" data-testid="rate-feature-city-link">
              See rates in your city
            </Link>
          </div>
          <div className="flex gap-8 sm:gap-10 shrink-0">
            <div>
              <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-stone-400">Gold 999</p>
              <p className="font-heading text-3xl font-semibold gold-num mt-1" data-testid="rate-feature-gold">₹{Number(rate.gold_24k).toLocaleString("en-IN")}<span className="text-sm text-stone-400 font-normal">/g</span></p>
            </div>
            <div>
              <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-stone-400">Silver 999</p>
              <p className="font-heading text-3xl font-semibold gold-num mt-1" data-testid="rate-feature-silver">₹{Number(rate.silver).toLocaleString("en-IN")}<span className="text-sm text-stone-400 font-normal">/g</span></p>
            </div>
          </div>
        </div>
        <p className="text-[10px] font-mono text-stone-400 mt-2 text-right" data-testid="rate-feature-updated">{updated}</p>
      </section>
    );
  }

  return (
    <div className="bg-[#2A1A38] text-ivory/80" data-testid="rate-strip">
      <div className="container-x flex flex-wrap items-center justify-center gap-x-8 gap-y-1 py-2 text-xs">
        <span className="uppercase tracking-[0.2em] text-ivory/50">Today's rates</span>
        <span className="gold-num font-medium" data-testid="rate-strip-gold">{gold}</span>
        <span className="gold-num font-medium" data-testid="rate-strip-silver">{silver}</span>
        <span className="text-ivory/40 font-mono text-[10px]" data-testid="rate-strip-updated">{updated}</span>
        <Link to="/gold-rate" className="text-gold hover:text-gold-light underline underline-offset-4 decoration-gold/40 transition-colors" data-testid="rate-strip-city-link">
          Rates by city
        </Link>
      </div>
    </div>
  );
}
